"use client";

/**
 * App-wide replacement for native `window.confirm()`. Native confirms
 * block the JS event loop, look like a Chrome system dialog, can't be
 * styled to match the product, and break the back-button on some
 * mobile browsers.
 *
 * Usage:
 *
 *     const confirm = useConfirm();
 *     const ok = await confirm({
 *       title: "Publish to live site?",
 *       description: "Commits to GitHub and triggers a Vercel deploy.",
 *       confirmText: "Publish",
 *       variant: "default",
 *     });
 *     if (!ok) return;
 *
 * Mounted once at the Providers level — any component below can call
 * useConfirm() to get a single promise-returning function.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from "@digirank/ui";
import { AlertTriangle, Send, Trash2, Sparkles } from "lucide-react";

export type ConfirmVariant = "default" | "destructive" | "publish" | "ai";

export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

type Resolver = (value: boolean) => void;

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = React.createContext<ConfirmContextValue | null>(null);

const VARIANT_STYLES: Record<
  ConfirmVariant,
  { btn: string; iconBg: string; iconText: string; Icon: typeof AlertTriangle }
> = {
  default: {
    btn: "bg-slate-900 hover:bg-slate-800 text-white",
    iconBg: "bg-slate-100",
    iconText: "text-slate-700",
    Icon: AlertTriangle,
  },
  destructive: {
    btn: "bg-red-600 hover:bg-red-700 text-white",
    iconBg: "bg-red-100",
    iconText: "text-red-600",
    Icon: Trash2,
  },
  publish: {
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white",
    iconBg: "bg-emerald-100",
    iconText: "text-emerald-700",
    Icon: Send,
  },
  ai: {
    btn: "bg-purple-600 hover:bg-purple-700 text-white",
    iconBg: "bg-purple-100",
    iconText: "text-purple-700",
    Icon: Sparkles,
  },
};

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [opts, setOpts] = React.useState<ConfirmOptions | null>(null);
  const resolverRef = React.useRef<Resolver | null>(null);

  const confirm = React.useCallback((options: ConfirmOptions) => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handle = (value: boolean) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    // Resolve after the close animation kicks off so users see the
    // dialog dismiss before any follow-up UI appears.
    setTimeout(() => r?.(value), 0);
  };

  // Radix's onOpenChange fires for the X button, escape key, and
  // overlay click — treat all of those as "cancel" so callers always
  // get a Promise<boolean>.
  const onOpenChange = (next: boolean) => {
    if (!next) handle(false);
  };

  const variant = opts?.variant ?? "default";
  const style = VARIANT_STYLES[variant];
  const Icon = style.Icon;

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-start gap-4">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${style.iconBg}`}>
                <Icon className={`h-5 w-5 ${style.iconText}`} />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle>{opts?.title}</DialogTitle>
                {opts?.description && (
                  <DialogDescription className="mt-2 text-sm leading-relaxed">
                    {opts.description}
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => handle(false)}
            >
              {opts?.cancelText ?? "Cancel"}
            </Button>
            <Button
              className={style.btn}
              onClick={() => handle(true)}
              autoFocus
            >
              {opts?.confirmText ?? "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  }
  return ctx.confirm;
}
